import { hasActiveSession } from './authCheck';

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface AuthDebugResult {
  timestamp: string;
  checks: {
    hasActiveSession: boolean;
    getCurrentUser: {
      success: boolean;
      userId: string | null;
      error: string | null;
    };
    localStorage: {
      storageKey: string;
      hasToken: boolean;
      tokenStructure: 'valid' | 'invalid' | 'missing';
      expiresAt: string | null;
      isExpired: boolean | null;
    };
    environment: {
      supabaseUrl: string;
      projectRef: string;
      userAgent: string;
    };
  };
  recommendation: string;
}

/**
 * Comprehensive authentication debugging utility
 * This will help identify exactly why authentication is failing
 */
export async function debugAuthentication(): Promise<AuthDebugResult> {
  const timestamp = new Date().toISOString();

  // 1. Check hasActiveSession()
  const hasSessionResult = hasActiveSession();

  // 2. Check getCurrentUser() directly
  const getCurrentUserResult = {
    success: false,
    userId: null as string | null,
    error: null as string | null,
  };

  try {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) {
      getCurrentUserResult.error = `${error.message} (${error.status})`;
    } else if (user) {
      getCurrentUserResult.success = true;
      getCurrentUserResult.userId = user.id;
    } else {
      getCurrentUserResult.error = 'No user object returned';
    }
  } catch (error: any) {
    getCurrentUserResult.error = `Exception: ${error.message}`;
  }

  // 3. Check localStorage details
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  let projectRef: string;

  if (supabaseUrl.includes('localhost') || supabaseUrl.includes('127.0.0.1')) {
    projectRef = 'localhost';
  } else {
    const match = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/);
    projectRef = match ? match[1] : '';
  }

  const storageKey = `sb-${projectRef}-auth-token`;
  const sessionData = localStorage.getItem(storageKey);

  const localStorageResult = {
    storageKey,
    hasToken: !!sessionData,
    tokenStructure: 'missing' as 'valid' | 'invalid' | 'missing',
    expiresAt: null as string | null,
    isExpired: null as boolean | null,
  };

  if (sessionData) {
    try {
      const session = JSON.parse(sessionData);
      if (session?.access_token && session?.expires_at) {
        localStorageResult.tokenStructure = 'valid';
        localStorageResult.expiresAt = new Date(session.expires_at * 1000).toISOString();
        localStorageResult.isExpired = new Date(session.expires_at * 1000) <= new Date();
      } else {
        localStorageResult.tokenStructure = 'invalid';
      }
    } catch {
      localStorageResult.tokenStructure = 'invalid';
    }
  }

  // 4. Generate recommendation
  let recommendation: string;

  if (!hasSessionResult && !getCurrentUserResult.success) {
    if (!localStorageResult.hasToken) {
      recommendation = 'CRITICAL: No session token in localStorage. User needs to sign in again.';
    } else if (localStorageResult.tokenStructure === 'invalid') {
      recommendation =
        'CRITICAL: Malformed session token in localStorage. Clear storage and re-authenticate.';
    } else if (localStorageResult.isExpired) {
      recommendation = 'CRITICAL: Session token expired. User needs to sign in again.';
    } else {
      recommendation = `CRITICAL: Valid localStorage token but Supabase API failing. Error: ${getCurrentUserResult.error}`;
    }
  } else if (hasSessionResult && !getCurrentUserResult.success) {
    recommendation = `API FAILURE: localStorage valid but Supabase API failing. Error: ${getCurrentUserResult.error}`;
  } else if (!hasSessionResult && getCurrentUserResult.success) {
    recommendation = 'SYNC ISSUE: Supabase API has valid user but localStorage check failing.';
  } else {
    recommendation = 'SUCCESS: Both checks passed - authentication should work.';
  }

  return {
    timestamp,
    checks: {
      hasActiveSession: hasSessionResult,
      getCurrentUser: getCurrentUserResult,
      localStorage: localStorageResult,
      environment: {
        supabaseUrl,
        projectRef,
        userAgent: navigator.userAgent,
      },
    },
    recommendation,
  };
}

/**
 * Emergency authentication fix - attempts to resolve common auth issues
 */
export async function emergencyAuthFix(): Promise<{
  success: boolean;
  action: string;
  error?: string;
}> {
  try {
    // Step 1: Clear potentially corrupted localStorage
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    let projectRef: string;

    if (supabaseUrl.includes('localhost') || supabaseUrl.includes('127.0.0.1')) {
      projectRef = 'localhost';
    } else {
      const match = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/);
      projectRef = match ? match[1] : '';
    }

    // Clear all auth-related storage
    const allKeys = Object.keys(localStorage);
    allKeys.forEach((key) => {
      if (key.startsWith(`sb-${projectRef}-`)) {
        localStorage.removeItem(key);
      }
    });

    // Step 2: Refresh session from Supabase
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      return {
        success: false,
        action: 'Cleared corrupted localStorage',
        error: `Session refresh failed: ${sessionError.message}`,
      };
    }

    if (!session) {
      return {
        success: false,
        action: 'Cleared corrupted localStorage',
        error: 'No active session found - user needs to sign in',
      };
    }

    // Step 3: Verify the fix worked
    const debugResult = await debugAuthentication();

    if (debugResult.checks.hasActiveSession && debugResult.checks.getCurrentUser.success) {
      return {
        success: true,
        action: 'Successfully refreshed authentication state',
      };
    } else {
      return {
        success: false,
        action: 'Attempted refresh but issues remain',
        error: debugResult.recommendation,
      };
    }
  } catch (error: any) {
    return {
      success: false,
      action: 'Emergency fix failed',
      error: `Exception: ${error.message}`,
    };
  }
}

/**
 * Simple function to log auth debug info to console (for production debugging)
 */
export async function logAuthDebug(): Promise<void> {
  if (!import.meta.env.DEV) return;
  const result = await debugAuthentication();
  logger.group('🔍 Authentication Debug');
  logger.debug('Timestamp:', result.timestamp);
  logger.debug('Has Active Session:', result.checks.hasActiveSession);
  logger.debug('Get Current User:', result.checks.getCurrentUser);
  logger.debug('LocalStorage:', result.checks.localStorage);
  logger.debug('Environment:', result.checks.environment);
  logger.debug('🔧 Recommendation:', result.recommendation);
  logger.groupEnd();
}
